import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { apiClient } from '../../../services/api/client';
import { locationApi } from '../../../services/api';

const InviteUserModal = ({ onClose, onSuccess, roleOptions }) => {
    const [formData, setFormData] = useState({ email: '', firstName: '', lastName: '', role: 'User', locationId: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [locations, setLocations] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(true);

    // Location is only relevant for Manager and User roles
    const showLocationField = formData.role === 'User' || formData.role === 'Manager';

    useEffect(() => {
        const fetchLocations = async () => {
            try {
                setLocationsLoading(true);
                const response = await locationApi.getDropdown();
                const data = response?.data ?? response?.Data ?? [];
                const success = response?.success ?? response?.Success ?? false;
                if (success && data) {
                    setLocations(data);
                }
            } catch (err) {
                console.error('Failed to fetch locations:', err);
            } finally {
                setLocationsLoading(false);
            }
        };
        fetchLocations();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = { ...prev, [name]: value };
            // Clear locationId when switching to an admin role
            if (name === 'role' && value !== 'User' && value !== 'Manager') {
                updated.locationId = '';
            }
            return updated;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);
        try {
            const payload = { ...formData };
            // Only send locationId if one is selected and role requires it
            if (!payload.locationId || !showLocationField) {
                delete payload.locationId;
            }
            const response = await apiClient.post('/users/invite', payload);
            if (response.success) { onSuccess(); }
            else { setError(response.message || 'Failed to send invitation'); }
        } catch (err) { setError(err.message || 'Failed to send invitation'); }
        finally { setLoading(false); }
    };

    const isValid = formData.email && formData.firstName && formData.lastName;

    return (
        <div className='fixed inset-0 z-[1200] flex items-center justify-center'>
            <div className='absolute inset-0 bg-black/50' onClick={onClose} />
            <div className='relative bg-card rounded-lg shadow-xl w-full max-w-md mx-4'>
                <div className='flex items-center justify-between p-4 border-b border-border'>
                    <h2 className='text-lg font-semibold'>Invite User</h2>
                    <button onClick={onClose} className='p-2 hover:bg-muted rounded-md'><Icon name='X' size={20} /></button>
                </div>
                <form onSubmit={handleSubmit} className='p-4 space-y-4'>
                    {error && (<div className='alert alert-error'><Icon name='AlertCircle' size={16} /><span>{error}</span></div>)}
                    <div><label className='block text-sm font-medium mb-1'>Email *</label><input type='email' name='email' value={formData.email} onChange={handleChange} className='input w-full' placeholder='user@example.com' required /></div>
                    <div className='grid grid-cols-2 gap-4'>
                        <div><label className='block text-sm font-medium mb-1'>First Name *</label><input type='text' name='firstName' value={formData.firstName} onChange={handleChange} className='input w-full' required /></div>
                        <div><label className='block text-sm font-medium mb-1'>Last Name *</label><input type='text' name='lastName' value={formData.lastName} onChange={handleChange} className='input w-full' required /></div>
                    </div>
                    <div><label className='block text-sm font-medium mb-1'>Role *</label><select name='role' value={formData.role} onChange={handleChange} className='select w-full'>{roleOptions.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}</select></div>
                    {showLocationField && (
                        <div>
                            <label className='block text-sm font-medium mb-1'>Location</label>
                            <select name='locationId' value={formData.locationId} onChange={handleChange} className='select w-full' disabled={locationsLoading}>
                                <option value=''>
                                    {locationsLoading ? 'Loading locations...' : 'Select a location (optional)'}
                                </option>
                                {locations.map(loc => (
                                    <option key={loc.id} value={loc.id}>{loc.name}{loc.code ? ` (${loc.code})` : ''}</option>
                                ))}
                            </select>
                            <p className='text-xs text-muted-foreground mt-1'>The user will be granted access to this location when they accept the invitation.</p>
                        </div>
                    )}
                    <div className='flex justify-end gap-3 pt-4 border-t border-border'>
                        <button type='button' onClick={onClose} className='btn btn-outline' disabled={loading}>Cancel</button>
                        <button type='submit' className='btn btn-primary' disabled={loading || !isValid}>{loading ? 'Sending...' : 'Send Invitation'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default InviteUserModal;

